/**
 * DocumentTypeDialog · sub-dialogs — extraits du fichier principal
 * (~3000 lignes) au cours de l'iter208 (2026-02-28) pour désencombrer
 * `DocumentTypeDialog.jsx` sans aucun changement visuel.
 *
 * 3 composants strictement identiques à leur ancienne version inlinée :
 *   • PropagationChoiceDialog   (spec rev134, 2026-02-06)
 *   • ModeChangeConfirmDialog   (spec rev147, 2026-02-07)
 *   • ResponsablesPropagationDialog (spec iter118b, Feb 2026)
 *
 * Contract : chaque composant reste `default-export-less` (named export
 * uniquement) — le parent les importe par déstructuration.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw, FilePlus2, Users, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";


/**
 * PropagationChoiceDialog — spec rev134 (2026-02-06).
 * Modale de confirmation présentée à l'admin AVANT d'enregistrer une
 * modification structurelle sur un type de document existant qui a
 * au moins un document actif. Deux cartes cliquables côte à côte
 * proposent le comportement à adopter :
 *   • « Propager » (vert)      : PATCH avec `?propagate=true`.
 *   • « Futurs seulement » (bleu) : PATCH avec `?propagate=false`.
 */
export function PropagationChoiceDialog({ open, onOpenChange, affectedCount, saving, onChoose }) {
  const [hovering, setHovering] = useState(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid="propagation-choice-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {affectedCount} document{affectedCount > 1 ? "s" : ""} utilise{affectedCount > 1 ? "nt" : ""} ce type
          </DialogTitle>
          <div className="text-[12.5px] mt-1" style={{ color: "#9CA3AF" }}>
            Comment appliquer cette modification ?
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          {/* Carte 1 — Propager (vert) */}
          <button
            type="button"
            disabled={saving}
            onClick={() => onChoose(true)}
            onMouseEnter={() => setHovering("propagate")}
            onMouseLeave={() => setHovering(null)}
            className="text-left rounded-lg p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "#0F2A1C",
              border: `1.5px solid ${hovering === "propagate" ? "#4ADE80" : "#22C55E"}`,
              boxShadow: hovering === "propagate" ? "0 0 24px rgba(34,197,94,0.30)" : "none",
            }}
            data-testid="propagate-choice-propagate"
          >
            <div className="flex items-start gap-3">
              <div
                className="rounded-lg shrink-0 flex items-center justify-center"
                style={{
                  width: 40, height: 40,
                  background: "rgba(34,197,94,0.14)",
                  border: "1px solid rgba(34,197,94,0.35)",
                }}
              >
                <RefreshCw size={20} strokeWidth={2.2} style={{ color: "#4ADE80" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[15px]" style={{ color: "#EDEDED", letterSpacing: "-0.01em" }}>
                  Propager
                </div>
                <div className="text-[12px] mt-0.5" style={{ color: "#86EFAC" }}>
                  Aux {affectedCount} document{affectedCount > 1 ? "s" : ""} existant{affectedCount > 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </button>
          {/* Carte 2 — Futurs seulement (bleu) */}
          <button
            type="button"
            disabled={saving}
            onClick={() => onChoose(false)}
            onMouseEnter={() => setHovering("future")}
            onMouseLeave={() => setHovering(null)}
            className="text-left rounded-lg p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "#0F1E2E",
              border: `1.5px solid ${hovering === "future" ? "#38BDF8" : "#0078D4"}`,
              boxShadow: hovering === "future" ? "0 0 24px rgba(0,120,212,0.30)" : "none",
            }}
            data-testid="propagate-choice-future"
          >
            <div className="flex items-start gap-3">
              <div
                className="rounded-lg shrink-0 flex items-center justify-center"
                style={{
                  width: 40, height: 40,
                  background: "rgba(0,120,212,0.14)",
                  border: "1px solid rgba(0,120,212,0.35)",
                }}
              >
                <FilePlus2 size={20} strokeWidth={2.2} style={{ color: "#5AB0F0" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[15px]" style={{ color: "#EDEDED", letterSpacing: "-0.01em" }}>
                  Futurs seulement
                </div>
                <div className="text-[12px] mt-0.5" style={{ color: "#93CDF0" }}>
                  Documents existants inchangés
                </div>
              </div>
            </div>
          </button>
        </div>
        <DialogFooter className="mt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="propagate-cancel"
          >
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ModeChangeConfirmDialog — spec rev147 (2026-02-07).
 * Modale de confirmation avant de basculer le mode workflow d'un type
 * (Avec étapes ↔ Sans étape) après création. Message adapté selon
 * la direction :
 *   • → « Sans étape » : rappelle que l'historique des étapes des
 *     documents existants sera CONSERVÉ en lecture seule.
 *   • → « Avec étapes » : rappelle qu'une modale de propagation
 *     supplémentaire sera présentée à la sauvegarde pour choisir
 *     entre appliquer aux docs existants ou seulement aux futurs.
 */
export function ModeChangeConfirmDialog({ open, onOpenChange, target, affectedCount, onConfirm }) {
  const goingSimple = target === "simple";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid="mode-change-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {goingSimple ? "Basculer en mode Sans étape ?" : target === "dossier" ? "Basculer en mode Dossier ?" : "Basculer en mode Avec étapes ?"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[13px] leading-relaxed mt-2" style={{ color: "#D1D5DB" }}>
          {goingSimple ? (
            <>
              Les {affectedCount > 0 ? `${affectedCount} document${affectedCount > 1 ? "s" : ""} existant${affectedCount > 1 ? "s" : ""} de ce type ` : "documents existants de ce type "}
              <strong style={{ color: "#F2F2F2" }}>conserveront leur historique d'étapes en lecture seule</strong>
              {" "}— jamais supprimé, jamais modifié. Les <strong style={{ color: "#F2F2F2" }}>nouveaux documents</strong> créés après cette bascule n'auront <strong style={{ color: "#F2F2F2" }}>pas d'étapes</strong>.
            </>
          ) : (
            <>
              Vous êtes sur le point d'ajouter un workflow à étapes à ce type.
              {affectedCount > 0 && (
                <>
                  {" "}Après avoir enregistré, vous choisirez si les <strong style={{ color: "#F2F2F2" }}>{affectedCount} document{affectedCount > 1 ? "s" : ""} existant{affectedCount > 1 ? "s" : ""}</strong> reçoivent ces étapes ou restent inchangés.
                </>
              )}
            </>
          )}
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="mode-change-confirm-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            className="text-white"
            style={{
              background: goingSimple ? "#8B5CF6" : "#0078D4",
              border: "none",
            }}
            data-testid="mode-change-confirm-ok"
          >
            {goingSimple ? "Confirmer — Sans étape" : "Confirmer — Avec étapes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * ResponsablesPropagationDialog — spec iter118b (Feb 2026).
 * Modale dédiée à la propagation d'un changement de LISTE de
 * responsables sur une (ou plusieurs) étape(s) d'un type de document
 * possédant des documents actifs.
 *
 * Contenu :
 *   • Pour chaque étape modifiée : chips (avatar + nom) de la nouvelle
 *     liste de responsables.
 *   • Nombre de documents impactés (compte fourni par le parent via
 *     ``affectedCount``).
 *   • Accordéon dépliable listant les documents concernés (référence +
 *     titre, chargés à la volée depuis GET /document-types/{id}/impact-docs).
 *   • Deux boutons : Annuler · « Propager à N documents ».
 *
 * Cohérence : on NE propose PAS d'option « Futurs seulement » (spec
 * iter118b — toujours proposer la propagation avec ce preview).
 */
export function ResponsablesPropagationDialog({
  open, onOpenChange, typeId, typeName, affectedCount, changes, saving, onConfirm,
}) {
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [docs, setDocs] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);

  // Charge la liste des utilisateurs (pour résoudre les ids → nom/initiales)
  // au premier open. Cache pour la durée de vie du dialog.
  useEffect(() => {
    if (!open || users.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/users");
        const items = (data?.items || data || []).filter((u) => !u.is_deleted);
        if (!cancelled) setUsers(items);
      } catch {
        // silencieux — les chips tomberont sur des ids bruts en fallback
      }
    })();
    return () => { cancelled = true; };
  }, [open, users.length]);

  // Reset l'accordéon à chaque ouverture — évite d'afficher la liste
  // d'un doc précédent quand la modale est réutilisée pour un autre type.
  useEffect(() => {
    if (!open) { setExpanded(false); setDocs(null); }
  }, [open]);

  const usersById = React.useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);

  const nameOf = (uid) => {
    const u = usersById[uid];
    if (!u) return uid;
    return (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || uid);
  };
  const initialsOf = (uid) => {
    const u = usersById[uid];
    if (!u) return "?";
    const first = (u.first_name || "").trim();
    const last = (u.last_name || "").trim();
    return ((first[0] || "") + (last[0] || "")).toUpperCase() || (u.email || "?")[0].toUpperCase();
  };

  const toggleExpanded = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && docs === null && typeId) {
      setDocsLoading(true);
      try {
        const { data } = await api.get(`/document-types/${typeId}/impact-docs`);
        setDocs(data?.items || []);
      } catch {
        setDocs([]);
      } finally {
        setDocsLoading(false);
      }
    }
  };

  const stepsCount = (changes || []).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid="resp-propagation-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] flex items-center gap-2">
            <Users size={16} style={{ color: "#5b5fc7" }} />
            Propager les responsables
          </DialogTitle>
          <div className="text-[12.5px] mt-1" style={{ color: "#9CA3AF" }}>
            {typeName ? <>Type <strong style={{ color: "#EDEDED" }}>« {typeName} »</strong> · </> : null}
            {stepsCount > 1
              ? `${stepsCount} étapes modifiées`
              : "1 étape modifiée"}
          </div>
        </DialogHeader>

        {/* Chips par étape */}
        <div className="mt-2 space-y-2.5" data-testid="resp-propagation-steps">
          {(changes || []).map((c, i) => (
            <div
              key={c.step_id || i}
              className="rounded-md p-3"
              style={{ background: "#1B1B1B", border: "1px solid #2A2A2A" }}
              data-testid={`resp-propagation-step-${i}`}
            >
              <div className="text-[10.5px] uppercase tracking-widest mb-2" style={{ color: "#9CA3AF" }}>
                Étape · <span style={{ color: "#EDEDED" }}>{c.label || "(sans nom)"}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {(!c.to_ids || c.to_ids.length === 0) ? (
                  <span className="text-xs italic" style={{ color: "#9a9a95" }}>
                    Aucun responsable — fallback créateur du document
                  </span>
                ) : (
                  c.to_ids.map((uid) => (
                    <span
                      key={uid}
                      className="inline-flex items-center gap-1 text-[11.5px] rounded-full pl-0.5 pr-2 py-0.5"
                      style={{
                        background: "#141414",
                        border: "1px solid #5b5fc7",
                        color: "#EFEFEF",
                      }}
                      data-testid={`resp-propagation-chip-${uid}`}
                    >
                      <span
                        className="inline-flex items-center justify-center rounded-full text-[9px] font-bold"
                        style={{ width: 18, height: 18, background: "#5b5fc7", color: "#FFFFFF" }}
                      >
                        {initialsOf(uid)}
                      </span>
                      <span className="truncate max-w-[160px]">{nameOf(uid)}</span>
                    </span>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Compteur documents impactés + accordéon */}
        <div className="mt-3 rounded-md overflow-hidden" style={{ background: "#1B1B1B", border: "1px solid #2A2A2A" }}>
          <button
            type="button"
            onClick={toggleExpanded}
            className="w-full flex items-center justify-between px-3 py-2 text-[12.5px] hover:bg-white/[0.03] transition"
            data-testid="resp-propagation-docs-toggle"
          >
            <span style={{ color: "#EDEDED" }}>
              <strong>{affectedCount}</strong> document{affectedCount > 1 ? "s" : ""} impacté{affectedCount > 1 ? "s" : ""}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#9CA3AF" }}>
              {expanded ? "Masquer la liste" : "Voir la liste"}
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </span>
          </button>
          {expanded && (
            <div
              className="max-h-[220px] overflow-y-auto border-t"
              style={{ borderColor: "#242424" }}
              data-testid="resp-propagation-docs-list"
            >
              {docsLoading ? (
                <div className="px-3 py-3 text-[11.5px] italic text-center" style={{ color: "#8B8F94" }}>
                  Chargement…
                </div>
              ) : !docs || docs.length === 0 ? (
                <div className="px-3 py-3 text-[11.5px] italic text-center" style={{ color: "#8B8F94" }}>
                  Aucun document actif trouvé.
                </div>
              ) : (
                docs.map((d) => (
                  <div
                    key={d.id}
                    className="grid grid-cols-[110px_1fr] items-center px-3 py-1.5 border-b text-[12px]"
                    style={{ borderColor: "#1F1F1F" }}
                    data-testid={`resp-propagation-doc-${d.id}`}
                  >
                    <span className="font-mono text-[11.5px]" style={{ color: "#5AB0F0" }}>
                      {d.reference}
                    </span>
                    <span className="truncate" style={{ color: "#EDEDED" }}>
                      {d.title}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="resp-propagation-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={saving}
            className="text-white border-0 disabled:opacity-50"
            style={{ background: "#5b5fc7" }}
            data-testid="resp-propagation-confirm"
          >
            {saving
              ? "Propagation…"
              : `Propager à ${affectedCount} document${affectedCount > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
